import { ApolloClient } from '@apollo/client';
import {
  ConversationMessage,
  ConversationSms,
  ConversationSmsSession,
} from '@provision/entity-types';
import { WebSocketEventBus } from '@provision/ui-real-time-events';
import { useEffect } from 'react';
import type { TaggedMessage } from './sms-session-grouping';
import {
  GET_OPEN_SMS_SESSION_QUERY,
  getOpenSmsSessionQueryVariables,
} from './sms-session.operations';

interface SmsSessionRealtimeOptions {
  client: ApolloClient<unknown>;
  wsEventBus: WebSocketEventBus;
  conversationId: string;
  showSmsSwitch: boolean;
  onPairComplete: (msg: TaggedMessage) => void;
}

/** Also called by the provider's startSession, whose response carries the same fields. */
export function patchSessionOpen(
  client: ApolloClient<unknown>,
  session: Pick<
    ConversationSmsSession,
    'conversationSmsSessionId' | 'conversationChannelId' | 'state'
  > & {
    openedAt?: string | null;
    externalId?: string | null;
  },
  conversationId: string,
): void {
  client.writeQuery({
    query: GET_OPEN_SMS_SESSION_QUERY,
    variables: getOpenSmsSessionQueryVariables(conversationId),
    data: {
      getConversationSmsSession: {
        records: [
          {
            conversationSmsSessionId: session.conversationSmsSessionId,
            conversationId,
            conversationChannelId: session.conversationChannelId,
            state: session.state,
            openedAt: session.openedAt ?? null,
            externalId: session.externalId ?? null,
          },
        ],
      },
    },
  });
}

function patchSessionEnded(client: ApolloClient<unknown>, conversationId: string): void {
  client.writeQuery({
    query: GET_OPEN_SMS_SESSION_QUERY,
    variables: getOpenSmsSessionQueryVariables(conversationId),
    data: {
      getConversationSmsSession: {
        records: [],
      },
    },
  });
}

export function useSmsSessionRealtime({
  client,
  wsEventBus,
  conversationId,
  showSmsSwitch,
  onPairComplete,
}: SmsSessionRealtimeOptions): void {
  useEffect(() => {
    if (!showSmsSwitch || !conversationId) {
      return;
    }

    const pendingMessages = new Map<string, ConversationMessage>();
    const pendingSmsTags = new Map<string, ConversationSms>();
    const emittedIds = new Set<string>();

    function maybeFlush(messageId: string): void {
      if (emittedIds.has(messageId)) return;
      const msg = pendingMessages.get(messageId);
      const sms = pendingSmsTags.get(messageId);
      if (!msg || !sms) return;
      pendingMessages.delete(messageId);
      pendingSmsTags.delete(messageId);
      emittedIds.add(messageId);
      onPairComplete({
        conversationMessageId: msg.conversationMessageId,
        createdAt: msg.createdAt,
        body: msg.body ?? null,
        senderChannelId: msg.senderChannelId ?? null,
        smsTags: [
          {
            conversationSmsSessionId: sms.conversationSmsSessionId,
            direction: sms.direction,
          },
        ],
      });
    }

    const sessionCreateSub = wsEventBus
      .onEntity<ConversationSmsSession>({
        name: 'ConversationSmsSession',
        operation: 'create',
      })
      .subscribe({
        next: ({ attributes }) => {
          if (attributes.conversationId !== conversationId) return;
          if (attributes.state !== 'open') return;
          patchSessionOpen(client, attributes, conversationId);
        },
      });

    const sessionUpdateSub = wsEventBus
      .onEntity<ConversationSmsSession>({
        name: 'ConversationSmsSession',
        operation: 'update',
      })
      .subscribe({
        next: ({ attributes }) => {
          if (attributes.conversationId !== conversationId) return;
          if (attributes.state === 'ended') {
            patchSessionEnded(client, conversationId);
          } else if (attributes.state === 'open') {
            patchSessionOpen(client, attributes, conversationId);
          }
        },
      });

    const messageCreateSub = wsEventBus
      .onEntity<ConversationMessage>({ name: 'ConversationMessage', operation: 'create' })
      .subscribe({
        next: ({ attributes }) => {
          if (attributes.conversationId !== conversationId) return;
          pendingMessages.set(attributes.conversationMessageId, attributes);
          maybeFlush(attributes.conversationMessageId);
        },
      });

    const smsCreateSub = wsEventBus
      .onEntity<ConversationSms>({ name: 'ConversationSms', operation: 'create' })
      .subscribe({
        next: ({ attributes }) => {
          pendingSmsTags.set(attributes.conversationMessageId, attributes);
          maybeFlush(attributes.conversationMessageId);
        },
      });

    return () => {
      sessionCreateSub.unsubscribe();
      sessionUpdateSub.unsubscribe();
      messageCreateSub.unsubscribe();
      smsCreateSub.unsubscribe();
      pendingMessages.clear();
      pendingSmsTags.clear();
      emittedIds.clear();
    };
  }, [client, wsEventBus, conversationId, showSmsSwitch, onPairComplete]);
}
